import type { Email } from "./interfaces";
import { escapeHtml } from "./emailBodyDisplay";

export type ReplyMode = "reply" | "reply_all";

export type ReplySelfContext = {
  /** Connected mailbox / BudyUser.email — used to detect sent-by-me */
  mailboxEmail?: string | null;
  /** JWT login email — used only to exclude self from recipients */
  loginEmail?: string | null;
};

const normalizeEmail = (email: string) =>
  extractEmailAddress(email).toLowerCase();

/** Extract bare address from `Name <user@host>` or plain email. */
export function extractEmailAddress(raw: string): string {
  const trimmed = raw.trim();
  const angle = trimmed.match(/<([^>]+)>/);
  if (angle?.[1]) return angle[1].trim();
  return trimmed;
}

export const parseEmailList = (
  value: string | string[] | null | undefined,
): string[] => {
  if (!value) return [];
  const parts = Array.isArray(value) ? value : value.split(",");
  return parts.map((e) => extractEmailAddress(e)).filter(Boolean);
};

const isSameAddress = (a: string, b: string) =>
  normalizeEmail(a) === normalizeEmail(b);

function collectSelfEmails(ctx: ReplySelfContext): string[] {
  const emails: string[] = [];
  for (const raw of [ctx.mailboxEmail, ctx.loginEmail]) {
    if (raw?.trim()) emails.push(extractEmailAddress(raw));
  }
  return [...new Set(emails)];
}

/** True when this message was sent from the user's connected mailbox (not login-only). */
function isFromMe(message: Email, ctx: ReplySelfContext): boolean {
  const sender = extractEmailAddress(message.from_email);
  if (ctx.mailboxEmail?.trim()) {
    return isSameAddress(sender, ctx.mailboxEmail);
  }
  if (ctx.loginEmail?.trim()) {
    return isSameAddress(sender, ctx.loginEmail);
  }
  return false;
}

const excludeSelf = (addresses: string[], selfEmails: string[]) =>
  addresses.filter(
    (addr) => !selfEmails.some((self) => isSameAddress(addr, self)),
  );

export function buildReplySubject(subject: string): string {
  const trimmed = subject.trim();
  if (!trimmed) return "Re:";
  if (/^re:\s*/i.test(trimmed)) return trimmed;
  return `Re: ${trimmed}`;
}

export function buildQuotedReplyTail(message: Email): string {
  // Strip tags, then decode entities for readable quote text. Re-escape before
  // embedding in HTML so entity-encoded payloads (e.g. &lt;script&gt;) cannot
  // become live markup in the composer.
  const plain = message.content
    .replace(/<br\s*\/?>/gi, "\n")
    .replace(/<\/p>/gi, "\n")
    .replace(/<[^>]+>/g, "")
    .replace(/&nbsp;/g, " ")
    .replace(/&amp;/g, "&")
    .replace(/&lt;/g, "<")
    .replace(/&gt;/g, ">")
    .trim();
  const quotedLines = plain
    .split("\n")
    .map((line) => `> ${escapeHtml(line)}`)
    .join("<br>");
  return (
    `<p>On ${escapeHtml(message.when)}, ${escapeHtml(message.from_email)} wrote:</p>` +
    `<blockquote>${quotedLines}</blockquote>`
  );
}

export const EMPTY_REPLY_COMPOSE_HTML = "<p><br></p>";

export function buildQuotedReplyHtml(message: Email): string {
  return `${EMPTY_REPLY_COMPOSE_HTML}${buildQuotedReplyTail(message)}`;
}

export function computeReplyRecipients(
  message: Email,
  mode: ReplyMode,
  selfContext: ReplySelfContext,
): { to: string[]; cc: string[] } {
  const selfEmails = collectSelfEmails(selfContext);
  const fromMe = isFromMe(message, selfContext);
  const toList = parseEmailList(message.to_email);
  const ccList = (message.cc_email ?? []).map((e) => extractEmailAddress(e));

  if (mode === "reply") {
    if (!fromMe) {
      return { to: [extractEmailAddress(message.from_email)], cc: [] };
    }
    return { to: excludeSelf(toList, selfEmails), cc: [] };
  }

  if (fromMe) {
    return {
      to: excludeSelf(toList, selfEmails),
      cc: excludeSelf(ccList, selfEmails),
    };
  }

  const to = [extractEmailAddress(message.from_email)];
  const cc = [
    ...new Set(
      excludeSelf([...toList, ...ccList], selfEmails).filter(
        (addr) => !isSameAddress(addr, message.from_email),
      ),
    ),
  ];
  return { to, cc };
}
